/**
 * `operating_hours` normalisation.
 *
 * Lebron and Atlas both type this field as an untyped JSON object, so the exact
 * shape is not guaranteed by the schema. What the backend actually emits (as of
 * 2026-09, across all 260 stores) is one of two things:
 *
 *   {}                                              — 66 stores
 *   { "Mon": { "open": "08:30", "close": "21:00" },  — 194 stores
 *     ... "Sun": {...}, "holiday": {...} }
 *
 * Note the `holiday` key alongside the weekdays: keys that are not weekdays are
 * collected as `extras` rather than failing the whole record, because rejecting
 * a payload over one unrecognised key discards seven perfectly good days.
 */

export interface HoursWindow {
  open: string;
  close: string;
}

export interface DayHours {
  day: string;
  /** Empty when the store is closed that day. */
  windows: HoursWindow[];
}

/** A non-weekday entry, e.g. `holiday`. Windows may be empty if unset. */
export interface ExtraHours {
  key: string;
  windows: HoursWindow[];
}

export const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DAY_ALIASES: Record<string, string> = {
  mon: "monday",
  tue: "tuesday",
  tues: "tuesday",
  wed: "wednesday",
  thu: "thursday",
  thur: "thursday",
  thurs: "thursday",
  fri: "friday",
  sat: "saturday",
  sun: "sunday",
  // Some producers key by weekday index. Python's convention (0 = Monday) is
  // what the backend uses elsewhere, so follow it here.
  "0": "monday",
  "1": "tuesday",
  "2": "wednesday",
  "3": "thursday",
  "4": "friday",
  "5": "saturday",
  "6": "sunday",
};

function canonicalDay(key: string): string | null {
  const lower = key.trim().toLowerCase();
  if (DAY_ORDER.includes(lower)) return lower;
  return DAY_ALIASES[lower] ?? null;
}

function asTime(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return /^\d{1,2}:\d{2}(:\d{2})?$/.test(value.trim()) ? value.trim() : null;
}

/** Reads one entry's value, which may be a single window, a list, or "closed". */
function parseWindows(value: unknown): HoursWindow[] | null {
  if (value === null || value === undefined) return [];

  // An explicit closed marker, e.g. "closed".
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "closed" ? [] : null;
  }

  if (Array.isArray(value)) {
    // A bare ["07:00", "22:00"] pair.
    if (value.length === 2 && asTime(value[0]) && asTime(value[1])) {
      return [{ open: asTime(value[0])!, close: asTime(value[1])! }];
    }
    const windows: HoursWindow[] = [];
    for (const entry of value) {
      const parsed = parseWindows(entry);
      if (parsed === null) return null;
      windows.push(...parsed);
    }
    return windows;
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (record.closed === true) return [];

    const open =
      asTime(record.open) ?? asTime(record.opening_time) ?? asTime(record.from);
    const close =
      asTime(record.close) ??
      asTime(record.closing_time) ??
      asTime(record.to) ??
      asTime(record.until);

    if (open && close) return [{ open, close }];
    // `{}` means the entry exists but carries no hours — common for `holiday`.
    return Object.keys(record).length === 0 ? [] : null;
  }

  return null;
}

export type NormalisedHours =
  | { kind: "empty" }
  | { kind: "parsed"; days: DayHours[]; extras: ExtraHours[] }
  | { kind: "unrecognised"; raw: unknown };

/**
 * Turns the raw `operating_hours` object into weekday-ordered rows.
 *
 * Non-weekday keys become `extras` instead of invalidating the record. A
 * *weekday* whose value cannot be parsed still fails the whole payload to
 * `unrecognised`, since that means a genuinely unfamiliar shape — callers fall
 * back to the store's default window in that case rather than showing nothing.
 */
export function normaliseOperatingHours(raw: unknown): NormalisedHours {
  if (raw === null || raw === undefined) return { kind: "empty" };
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return { kind: "unrecognised", raw };
  }

  const entries = Object.entries(raw as Record<string, unknown>);
  if (entries.length === 0) return { kind: "empty" };

  const byDay = new Map<string, HoursWindow[]>();
  const extras: ExtraHours[] = [];

  for (const [key, value] of entries) {
    const day = canonicalDay(key);

    if (day === null) {
      // Not a weekday — keep it as an extra, with whatever we could read.
      const windows = parseWindows(value);
      extras.push({ key, windows: windows ?? [] });
      continue;
    }

    const windows = parseWindows(value);
    if (windows === null) return { kind: "unrecognised", raw };
    byDay.set(day, windows);
  }

  const days = DAY_ORDER.filter((day) => byDay.has(day)).map((day) => ({
    day,
    windows: byDay.get(day)!,
  }));

  // Extras alone are not a schedule; without a single weekday there is nothing
  // to lay out, so hand the payload back for raw display.
  if (days.length === 0) return { kind: "unrecognised", raw };

  return { kind: "parsed", days, extras };
}

export function dayLabel(day: string): string {
  return day.charAt(0).toUpperCase() + day.slice(1);
}
